import {
  getNotificationPreferencesFn,
  updateNotificationPreferencesFn,
} from "@/actions/push/handler";
import { ActiveSessions } from "@/components/auth/ActiveSessions";
import { PushNotificationToggle } from "@/components/push-notification-toggle";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Bell, Settings2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ["notificationPreferences"],
    queryFn: () => getNotificationPreferencesFn(),
  });

  const [localPrefs, setLocalPrefs] = useState({
    notifNewContent: true,
    notifProgramReminders: true,
    notifWeekComplete: true,
  });

  useEffect(() => {
    if (preferences?.body) {
      setLocalPrefs(preferences.body);
    }
  }, [preferences]);

  const { mutate: updatePrefs, isPending } = useMutation({
    mutationFn: (data: typeof localPrefs) =>
      updateNotificationPreferencesFn({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificationPreferences"] });
      toast.success("Bildirim tercihleri kaydedildi");
    },
    onError: () => {
      toast.error("Tercihler kaydedilirken bir hata oluştu");
    },
  });

  const handleToggle = (key: keyof typeof localPrefs) => {
    setLocalPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="container mx-auto max-w-2xl px-4 pb-20">
        <h1 className="mb-8 font-bold text-2xl text-foreground">Ayarlar</h1>

        <div className="space-y-6">
          <section className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-foreground text-lg">
                Bildirim Durumu
              </h2>
            </div>
            <p className="mb-4 text-muted-foreground text-sm">
              Anlık bildirim alabilmek için bu cihazda bildirimleri
              etkinleştirin.
              {typeof Notification !== "undefined" &&
                Notification.permission === "denied" && (
                  <span className="mt-1 block text-amber-500 text-xs">
                    Bildirimler tarayıcı ayarlarından engellenmiş. Açmak için
                    tarayıcı site ayarlarını değiştirin.
                  </span>
                )}
            </p>
            <PushNotificationToggle />
          </section>

          <section className="rounded-xl border border-border bg-card p-6">
            <div className="mb-6 flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              <h2 className="font-semibold text-foreground text-lg">
                Bildirim Tercihleri
              </h2>
            </div>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="notifNewContent"
                    className="font-medium text-sm"
                  >
                    Yeni İçerik
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    Yeni yayınlanan makale, program ve ürünlerden haberdar olun.
                  </p>
                </div>
                <Switch
                  id="notifNewContent"
                  disabled={isLoading}
                  checked={localPrefs.notifNewContent}
                  onCheckedChange={() => handleToggle("notifNewContent")}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="notifProgramReminders"
                    className="font-medium text-sm"
                  >
                    Program Hatırlatıcıları
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    Programınızdaki aksamalar, yarım kalan antrenmanlar ve
                    önemli uyarılar hakkında bildirim alın.
                  </p>
                </div>
                <Switch
                  id="notifProgramReminders"
                  disabled={isLoading}
                  checked={localPrefs.notifProgramReminders}
                  onCheckedChange={() => handleToggle("notifProgramReminders")}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="notifWeekComplete"
                    className="font-medium text-sm"
                  >
                    Hafta Tamamlama
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    Programınızda bir haftayı tamamladığınızda kutlama bildirimi
                    alın.
                  </p>
                </div>
                <Switch
                  id="notifWeekComplete"
                  disabled={isLoading}
                  checked={localPrefs.notifWeekComplete}
                  onCheckedChange={() => handleToggle("notifWeekComplete")}
                />
              </div>

              <div className="pt-2">
                <Button
                  className="w-full"
                  disabled={isLoading || isPending}
                  onClick={() => updatePrefs(localPrefs)}
                >
                  {isPending ? "Kaydediliyor..." : "Tercihleri Kaydet"}
                </Button>
              </div>
            </div>
          </section>

          <ActiveSessions />
        </div>
      </div>
    </div>
  );
}
